import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import ProductGroupSelect from '@/components/Form/Shared/ProductGroupSelect'
import type { DatacentralHookFormDataItem } from '@/components/ui/form-field'
import { DatacentralHookForm } from '@/components/ui/form-field'
import { CONTACT_ASSIGNEE_DEPARTMENT_IDS } from '@/constants/departments'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    createContactSource,
    getContactByObjectId,
    checkEmailExists,
    getContactSource,
    updateContactSource,
} from '@/modules/Marketing/services'
import { ContactSourceCreatePayload } from '@/modules/Marketing/types/payload'
import { ContactSource } from '@/modules/Marketing/types/response'
import { ContactStatusTypes } from '@/modules/Marketing/types/utils'
import { paths } from '@/utils/path'
import { Check, Minus } from 'lucide-react'
import toast from 'react-hot-toast'
import { useState } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Dialog } from 'vnetwork-ui'
import { ChatRoom } from '../../types/response'

interface SyncToMarketingModalProps {
    open: boolean
    onClose: () => void
    room: ChatRoom
    /** Pre-fill overrides from Block action (status, assignee, notes required) */
    defaultOverrides?: {
        contact_source_status?: string
        contact_source_assignee_ids?: number[]
    }
    /** Callback after successful sync (e.g. markChatDone + status change) */
    onAfterSync?: () => void | Promise<void>
}

/** Preview item: green check if filled, gray dash if empty */
const PreviewItem = ({
    label,
    value,
}: {
    label: string
    value: string | null | undefined
}) => (
    <div className="flex items-center gap-2 text-sm">
        {value ? (
            <Check size={14} className="text-green-500 flex-shrink-0" />
        ) : (
            <Minus size={14} className="text-gray-300 flex-shrink-0" />
        )}
        <span className="text-gray-500 min-w-[100px]">{label}:</span>
        <span
            className={
                value ? 'text-gray-800 font-medium' : 'text-gray-400 italic'
            }
        >
            {value || '-'}
        </span>
    </div>
)

const SyncToMarketingModal: React.FC<SyncToMarketingModalProps> = ({
    open,
    onClose,
    room,
    defaultOverrides,
    onAfterSync,
}) => {
    const { t } = useTranslation('livechat')
    const navigate = useNavigate()

    const isBlockMode =
        defaultOverrides?.contact_source_status === 'un-qualified-lead'

    // Extract customer (userType === 'user') from room
    const customer = room.users?.find((u) => u.userType === 'user')

    const formControl = useForm<ContactSourceCreatePayload>({
        defaultValues: {
            contact_source_name: customer?.name || '',
            contact_source_email: customer?.email || '',
            contact_source_phone: customer?.phoneNumber || '',
            contact_source_position: '',
            contact_source_channel: 'livechat',
            contact_source_company: '',
            contact_source_profile: '',
            contact_source_website: room.sourceOrigin || '',
            contact_source_notes: '',
            contact_source_status:
                defaultOverrides?.contact_source_status || 'UNCLASSIFIED',
            contact_source_industry: '',
            contact_source_interested_services: [],
            contact_source_object_id: room.id,
            contact_source_object_type: 'Internal-Livechat',
            contact_source_referrer_id: null,
            contact_source_referrer_type: null,
            contact_source_assignee_ids:
                defaultOverrides?.contact_source_assignee_ids || null,
            website_referrer: room.sourceUrl || '',
            company_size: '',
            deployment_time: '',
            specific_needs: '',
            customer_lead_type: 'internal_livechat',
        },
    })

    const [isSyncing, setIsSyncing] = useState(false)

    const { mutateAsync: syncLead, isPending } = useMutationWithHandler(
        { mutationFn: createContactSource },
        {
            reactQueryKeys: QUERY_KEYS.MARKETING,
            successMessage: t('sync-success'),
        }
    )

    const { mutateAsync: updateLead } = useMutationWithHandler(
        { mutationFn: updateContactSource },
        {
            reactQueryKeys: QUERY_KEYS.MARKETING,
            successMessage: t('sync-success'),
        }
    )

    const navigateToContact = (contactSourceId: number) => {
        onClose()
        navigate(`${paths.customerMkt}?contact_source_id=${contactSourceId}`)
    }

    const onSubmit = async (values: ContactSourceCreatePayload) => {
        // Block mode: validate notes required
        if (isBlockMode && !values.contact_source_notes?.trim()) {
            toast.error(t('block-reason-required'))
            return
        }

        try {
            setIsSyncing(true)

            // Step 1: Check if this livechat room was already synced
            const existingByObjectId = await getContactByObjectId(
                String(values.contact_source_object_id)
            )
            if (existingByObjectId?.contact_source_id) {
                // Block mode: update status + notes for existing contact
                if (isBlockMode) {
                    await updateLead({
                        contact_source_id: existingByObjectId.contact_source_id,
                        contact_source_name: values.contact_source_name,
                        contact_source_email: values.contact_source_email,
                        contact_source_phone: values.contact_source_phone,
                        contact_source_channel: values.contact_source_channel,
                        contact_source_company: values.contact_source_company,
                        contact_source_position: values.contact_source_position,
                        contact_source_website: values.contact_source_website,
                        contact_source_notes: values.contact_source_notes,
                        contact_source_status: values.contact_source_status,
                        contact_source_interested_services:
                            values.contact_source_interested_services,
                        contact_source_assignee_ids:
                            values.contact_source_assignee_ids || [],
                        website_referrer: values.website_referrer,
                    })
                }
                await onAfterSync?.()
                navigateToContact(existingByObjectId.contact_source_id)
                return
            }

            // Step 2: Check if email already exists in contact sources
            const email = values.contact_source_email?.trim()
            if (email) {
                const emailCheck = await checkEmailExists(email)
                if (emailCheck?.exists_in_contact_sources) {
                    // Find the existing contact by email to get its ID
                    const contactList = await getContactSource({
                        q: email,
                        page: 1,
                        page_size: 1,
                    })
                    const existing = contactList?.results?.find(
                        (c) => c.contact_source_email === email
                    )

                    if (existing?.contact_source_id) {
                        // Update existing contact with new livechat data
                        await updateLead({
                            contact_source_id: existing.contact_source_id,
                            contact_source_name: values.contact_source_name,
                            contact_source_email: email,
                            contact_source_phone: values.contact_source_phone,
                            contact_source_channel:
                                values.contact_source_channel,
                            contact_source_company:
                                values.contact_source_company,
                            contact_source_position:
                                values.contact_source_position,
                            contact_source_website:
                                values.contact_source_website,
                            contact_source_notes: values.contact_source_notes,
                            contact_source_status: values.contact_source_status,
                            contact_source_interested_services:
                                values.contact_source_interested_services,
                            contact_source_assignee_ids:
                                values.contact_source_assignee_ids || [],
                            website_referrer: values.website_referrer,
                        })
                        await onAfterSync?.()
                        navigateToContact(existing.contact_source_id)
                        return
                    }
                }
            }

            // Step 3: No duplicates — create new
            const result = await syncLead(values)
            const created = (result as { result?: ContactSource })?.result

            // Callback after successful sync (e.g. markChatDone for Block flow)
            await onAfterSync?.()

            if (created?.contact_source_id) {
                navigateToContact(created.contact_source_id)
            } else {
                onClose()
                navigate(paths.customerMkt)
            }
        } catch (error) {
            console.error('Sync to marketing failed', error)
        } finally {
            setIsSyncing(false)
        }
    }

    // Preview data — quick overview of what's pre-filled
    const previewItems = [
        { label: t('contact-source-name'), value: customer?.name },
        { label: 'Email', value: customer?.email },
        { label: t('contact-source-phone'), value: customer?.phoneNumber },
        { label: t('website-referrer'), value: room.sourceUrl },
        { label: 'Website', value: room.sourceOrigin },
    ]

    const filledCount = previewItems.filter((i) => !!i.value).length

    const formData: DatacentralHookFormDataItem<ContactSourceCreatePayload>[] =
        [
            {
                colSpan: 1,
                formItemProps: {
                    name: 'contact_source_name',
                    type: 'textfield',
                    label: t('contact-source-name'),
                    placeholder: t('contact-source-name'),
                    required: true,
                },
            },
            {
                colSpan: 1,
                formItemProps: {
                    name: 'contact_source_email',
                    type: 'textfield',
                    label: 'Email',
                    placeholder: 'Email',
                },
            },
            {
                colSpan: 1,
                formItemProps: {
                    name: 'contact_source_phone',
                    type: 'textfield',
                    label: t('contact-source-phone'),
                    placeholder: t('contact-source-phone'),
                },
            },
            {
                colSpan: 1,
                formItemProps: {
                    name: 'contact_source_company',
                    type: 'textfield',
                    label: t('company'),
                    placeholder: t('company'),
                },
            },
            {
                colSpan: 1,
                formItemProps: {
                    name: 'contact_source_position',
                    type: 'textfield',
                    label: t('position'),
                    placeholder: t('position'),
                },
            },
            {
                colSpan: 1,
                formItemProps: {
                    name: 'contact_source_channel',
                    type: 'textfield',
                    label: t('contact-source-channel'),
                    disabled: true,
                },
            },
            {
                colSpan: 1,
                formItemProps: {
                    name: 'website_referrer',
                    type: 'textfield',
                    label: t('website-referrer'),
                    placeholder: t('website-referrer'),
                },
            },
            {
                colSpan: 1,
                formItemProps: {
                    name: 'contact_source_website',
                    type: 'textfield',
                    label: 'Website',
                    placeholder: t('Website'),
                },
            },
            {
                colSpan: 1,
                className: 'md:col-span-2',
                formItemProps: {
                    name: 'contact_source_status',
                    type: 'select',
                    label: t('contact-source-status'),
                    placeholder: t('contact-source-status'),
                    options: ContactStatusTypes(t)?.slice(1),
                },
            },
            {
                colSpan: 1,
                className: 'md:col-span-2',
                renderEditComponent: (control) => (
                    <ProductGroupSelect
                        control={control}
                        name="contact_source_interested_services"
                        multiple
                        showLabel
                        label={t('contact-source-interested-services', {
                            ns: 'translation',
                        })}
                        placeholder={t('contact-source-interested-services', {
                            ns: 'translation',
                        })}
                    />
                ),
            },
            {
                colSpan: 1,
                className: 'md:col-span-2',
                formItemProps: {
                    name: 'specific_needs',
                    type: 'textarea',
                    label: t('specific-needs'),
                    placeholder: t('specific-needs'),
                },
            },
            {
                colSpan: 1,
                className: 'md:col-span-2',
                formItemProps: {
                    name: 'contact_source_notes',
                    type: 'textarea',
                    label: isBlockMode ? t('block-reason') : t('note'),
                    placeholder: isBlockMode
                        ? t('block-reason-placeholder')
                        : t('note'),
                    required: isBlockMode,
                },
            },
            {
                colSpan: 1,
                className: 'md:col-span-2',
                renderEditComponent: (control) => (
                    <EmployeeSelect
                        control={control}
                        name="contact_source_assignee_ids"
                        multiple
                        onlyActive
                        label="marketing:contact-source-assignee"
                        departmentIds={CONTACT_ASSIGNEE_DEPARTMENT_IDS}
                    />
                ),
            },
        ]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                isBlockMode ? t('block-and-sync') : t('sync-to-marketing-lead')
            }
            maxWidth="md"
        >
            <div className="space-y-4">
                {/* Preview card — shows pre-filled fields */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-blue-700">
                            {t('prefilled-from-chat')}
                        </span>
                        <span className="text-xs text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">
                            {filledCount}/{previewItems.length}
                        </span>
                    </div>
                    <div className="space-y-1.5">
                        {previewItems.map((item) => (
                            <PreviewItem
                                key={item.label}
                                label={item.label}
                                value={item.value}
                            />
                        ))}
                    </div>
                </div>

                {/* Form */}
                <DatacentralHookForm
                    formControl={
                        formControl as unknown as UseFormReturn<ContactSourceCreatePayload>
                    }
                    columns={2}
                    className="!grid-cols-1 md:!grid-cols-2"
                    data={formData}
                    actions={[
                        {
                            title: t('cancle-operation'),
                            variant: 'outline',
                            handler: onClose,
                            disabled: isPending || isSyncing,
                        },
                        {
                            title: t('sync-to-marketing'),
                            type: 'submit',
                            variant: 'default',
                            loading: isPending || isSyncing,
                            disabled: isPending || isSyncing,
                        },
                    ]}
                    onSubmit={onSubmit}
                />
            </div>
        </Dialog>
    )
}

export default SyncToMarketingModal
